import * as React from 'react';

import { cn } from '@heidi/utils';

import { useIsExperimentalDesignSystemButtons } from '../hooks/use-is-experimental-design-system-buttons';
import { useIsExperimentalDesignSystemForms } from '../hooks/use-is-experimental-design-system-forms';

import {
  buttonToneColourOverridesExperimental,
  buttonVariantsExperimental,
} from './button.experimental.styles';
import { buttonVariants } from './button.styles';
import type { ButtonRecipeOptions, ButtonRecipeProps } from './button.types';

/**
 * The single place a button recipe is resolved (DS-1575).
 *
 * **Package-internal.** Not exported from `../button/index.ts`: consumers style
 * these surfaces through props (`<AlertDialogAction variant="destructive">`),
 * so nothing outside the package needs to resolve a recipe itself.
 *
 * This deliberately replaces the inline `useIsExperimentalDesignSystemForms()`
 * + ternary idiom used by ~20 other components in this package. That idiom is
 * right where the rule *is* a ternary. It is not one here: resolution reads two
 * flags and does a tone-override lookup, and when four call sites each
 * re-derived that they drifted — only AlertDialog read the second flag, so a
 * success-toned `Calendar` nav button kept the legacy fill while the `Button`
 * beside it moved. Components whose recipe genuinely is a one-flag ternary
 * should keep using the inline idiom.
 *
 * Two flags decide what a button paints, and getting either wrong is a visible
 * mismatch:
 *
 * 1. `release-experimental-design-system-forms` picks the recipe
 *    (`buttonVariants` vs `buttonVariantsExperimental`).
 * 2. `release-experimental-design-system-buttons` layers the remaining DS-1202
 *    tone rework (success family only — destructive shipped on the forms flag in
 *    DS-1567). It only has meaning *inside* the experimental recipe, so it is
 *    gated on both.
 *
 * `Button` is not the only surface that needs this. `AlertDialogAction` /
 * `AlertDialogCancel`, `SkipLink` and the `Calendar` nav buttons all wear a
 * button recipe without rendering a `Button` — Radix and DayPicker own those
 * elements. Before this hook each re-derived the rule locally and they had
 * already drifted: only AlertDialog read the second flag, so a success-toned
 * `Calendar` nav button kept the legacy fill while the `Button` beside it moved.
 *
 * Consumers that own a real element spread the whole result, which also carries
 * the `data-slot` / `data-experimental-*` markers other recipes select on
 * (`button-group.styles.ts`). Consumers handing a class string to a third-party
 * slot take `className` alone.
 *
 * @example
 * // Owns its element — take the markers too.
 * const buttonProps = useButtonProps({ variant, size });
 * <AlertDialogPrimitive.Action
 *   {...buttonProps}
 *   className={cn(buttonProps.className, className)}
 * />
 *
 * @example
 * // Styling someone else's element — class string only.
 * const { className: navButtonClasses } = useButtonProps({ variant, size: 'xs' });
 */
export function useButtonProps({
  variant,
  size,
  iconOnly,
  flush,
  disableAutomaticTypographyClasses,
  disableAutomaticSvgSizing,
}: ButtonRecipeOptions = {}): ButtonRecipeProps {
  const isExperimentalForms = useIsExperimentalDesignSystemForms();
  const isExperimentalButtons = useIsExperimentalDesignSystemButtons();

  // Every dependency is a primitive, so this skips the cva build + tailwind-merge
  // on re-render — the whole cost of the hook — and hands back a stable object,
  // keeping `React.memo`'d children (e.g. `Calendar`) from re-rendering on it.
  return React.useMemo(() => {
    const variants = isExperimentalForms ? buttonVariantsExperimental : buttonVariants;
    // Appended after the base recipe (tailwind-merge lets the override win) but
    // before any caller `className`, so caller overrides still take precedence.
    const toneColourOverride =
      isExperimentalForms && isExperimentalButtons && variant
        ? buttonToneColourOverridesExperimental[variant]
        : undefined;

    return {
      'data-slot': 'button',
      'data-experimental-form': isExperimentalForms,
      'data-experimental-buttons': isExperimentalForms && isExperimentalButtons,
      className: cn(
        variants({
          variant,
          size,
          iconOnly,
          flush,
          disableAutomaticTypographyClasses,
          disableAutomaticSvgSizing,
        }),
        toneColourOverride
      ),
    };
  }, [
    isExperimentalForms,
    isExperimentalButtons,
    variant,
    size,
    iconOnly,
    flush,
    disableAutomaticTypographyClasses,
    disableAutomaticSvgSizing,
  ]);
}
